// ============================================================================
// ContractForm · formulário direto de criação de contrato.
// Coleta os campos canônicos do DadosContrato, valida no cliente com a MESMA
// lógica do chat (validarDados/alertaPreco) e dispara a geração real via
// onGerar(dados) -> callContractIntake({ dados, confirm: true }).
// Acesso já travado em `full` (rota + edge function); este componente não
// reimplementa segurança.
// ============================================================================

import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import {
  alertaPreco,
  extrairDadosDaMensagem,
  nomeProjetoValido,
  normalizarTranscricoes,
  validarDados,
  type DadosContrato,
} from "../lib/contratoSchema";
import { PLANOS, type Handoff } from "../lib/types";
import { supabase } from "../lib/supabase";
import styles from "./ContractForm.module.css";

/** Receita devolve tudo em CAIXA ALTA; deixa legível sem virar "Da" e "De". */
function tituloCase(v: string): string {
  const minusculas = new Set(["de", "da", "do", "das", "dos", "e", "em", "a", "o"]);
  return String(v ?? "")
    .toLowerCase()
    .split(/\s+/)
    .map((p, i) => (i > 0 && minusculas.has(p) ? p : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(" ")
    .trim();
}

interface DadosReceita {
  razao_social: string;
  endereco: string;
  numero: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  telefone: string;
  situacao: string;
}

/**
 * Consulta pública da Receita (BrasilAPI). É SUGESTÃO PARA CONFERIR, nunca
 * preenchimento automático: o endereço e o telefone que constam ali costumam
 * ser os da contabilidade, não os do cliente. E-mail não vem daqui de propósito
 * — é o campo que assina o contrato.
 */
async function consultarReceita(cnpj: string): Promise<DadosReceita | null> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 6000);
    const r = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`, { signal: ctrl.signal });
    clearTimeout(t);
    if (!r.ok) return null;
    const d = await r.json();
    return {
      razao_social: tituloCase(d.razao_social ?? ""),
      endereco: tituloCase([d.descricao_tipo_de_logradouro, d.logradouro].filter(Boolean).join(" ")),
      numero: String(d.numero ?? ""),
      bairro: tituloCase(d.bairro ?? ""),
      cidade: tituloCase(d.municipio ?? ""),
      uf: String(d.uf ?? "").toUpperCase(),
      cep: String(d.cep ?? "").replace(/\D/g, ""),
      telefone: String(d.ddd_telefone_1 ?? "").replace(/\D/g, ""),
      situacao: String(d.descricao_situacao_cadastral ?? ""),
    };
  } catch {
    return null; // sem Receita a tela segue normal
  }
}

type Tipo = "PJ" | "PF";

const UFS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

/** input type="date" devolve YYYY-MM-DD; o wrapper espera DD/MM/AAAA. */
function isoParaBR(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** Caminho inverso do isoParaBR: o input date precisa de YYYY-MM-DD. */
function brParaIso(br: string): string {
  const m = String(br).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : "";
}

/** DadosContrato -> estado do formulário (usado ao retomar rascunho e ao colar). */
function dadosParaF(dados: DadosContrato | undefined): Record<string, string> {
  const f: Record<string, string> = {};
  for (const [k, v] of Object.entries(dados ?? {})) {
    // transcricoes e array e mora em estado proprio (textarea), fora do `f`.
    if (k === "transcricoes") continue;
    if (v === undefined || v === null || String(v).trim() === "") continue;
    const s = k === "data_inicio" ? brParaIso(String(v)) : String(v);
    if (s) f[k] = s;
  }
  return f;
}

function initF(handoff: Handoff | null, dadosIniciais?: DadosContrato): Record<string, string> {
  const base: Record<string, string> = { nacionalidade: "Brasileira" };
  if (handoff) {
    if (handoff.nome_cliente) base.nome_empresarial = handoff.nome_cliente;
    if (handoff.plano_acordado && (PLANOS as string[]).includes(handoff.plano_acordado)) {
      base.plano = handoff.plano_acordado;
    }
    if (handoff.valor_acordado != null) base.valor_mensal = String(handoff.valor_acordado);
  }
  // O rascunho retomado vence o handoff: é o que a pessoa já tinha preenchido.
  return { ...base, ...dadosParaF(dadosIniciais) };
}

function tipoInicial(dados?: DadosContrato): Tipo {
  const cnpj = String(dados?.cnpj ?? "").replace(/\D/g, "");
  if (cnpj.length === 14) return "PJ";
  if (String(dados?.cpf ?? "").replace(/\D/g, "").length === 11 && !cnpj) return "PF";
  return "PJ";
}

export function ContractForm({
  handoff,
  submitting,
  onGerar,
  dadosIniciais,
  onDadosChange,
}: {
  handoff: Handoff | null;
  submitting: boolean;
  onGerar: (dados: DadosContrato) => void;
  /** Rascunho retomado: preenche o formulário na montagem. */
  dadosIniciais?: DadosContrato;
  /** Avisa o pai a cada mudança, para o autosave do rascunho. */
  onDadosChange?: (dados: DadosContrato) => void;
}) {
  const [tipo, setTipo] = useState<Tipo>(() => tipoInicial(dadosIniciais));
  const [f, setF] = useState<Record<string, string>>(() => initF(handoff, dadosIniciais));
  const [transcricoesRaw, setTranscricoesRaw] = useState<string>(
    () => (dadosIniciais?.transcricoes ?? []).join("\n")
  );
  const [colado, setColado] = useState("");
  const [resumoColado, setResumoColado] = useState<string | null>(null);
  const [naBase, setNaBase] = useState<Record<string, unknown> | null>(null);
  const [receita, setReceita] = useState<DadosReceita | null>(null);
  const [confirmar, setConfirmar] = useState(false);

  const set =
    (k: string) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setF((prev) => ({ ...prev, [k]: e.target.value }));

  const incluiSocial = (f.plano ?? "").includes("Social");
  // Só link de Google Docs conta: é o que o workflow do briefing consegue abrir.
  const transcricoes = useMemo(() => normalizarTranscricoes(transcricoesRaw), [transcricoesRaw]);

  const dados = useMemo<DadosContrato>(() => {
    const d: DadosContrato = {
      nome_completo: f.nome_completo?.trim() || undefined,
      cpf: f.cpf || undefined,
      email_assinatura: f.email_assinatura?.trim() || undefined,
      telefone: f.telefone || undefined,
      plano: f.plano || undefined,
      valor_mensal: f.valor_mensal ? Number(f.valor_mensal) : undefined,
      dia_pagamento: f.dia_pagamento || undefined,
      tempo_contrato: f.tempo_contrato ? Number(f.tempo_contrato) : undefined,
      data_inicio: f.data_inicio ? isoParaBR(f.data_inicio) : undefined,
      endereco: f.endereco || undefined,
      numero: f.numero || undefined,
      complemento: f.complemento || undefined,
      bairro: f.bairro || undefined,
      cidade: f.cidade || undefined,
      estado: f.estado || undefined,
      cep: f.cep || undefined,
      volume_posts: incluiSocial ? f.volume_posts || undefined : "-",
      nome_projeto: f.nome_projeto?.trim() || undefined,
      transcricoes: transcricoes.length ? transcricoes : undefined,
    };
    if (tipo === "PJ") {
      d.nome_empresarial = f.nome_empresarial || undefined;
      d.cnpj = f.cnpj || undefined;
    } else {
      d.rg = f.rg || undefined;
      d.profissao = f.profissao || undefined;
      d.estado_civil = f.estado_civil || undefined;
      d.nacionalidade = f.nacionalidade || "Brasileira";
    }
    return d;
  }, [f, tipo, incluiSocial, transcricoes]);

  const validacao = validarDados(dados);
  const aviso = alertaPreco(dados.plano as string, dados.valor_mensal);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!validacao.ok || submitting) return;
    setConfirmar(true);
  }

  // Autosave do rascunho vive no pai; aqui só avisamos que os dados mudaram.
  // Compara por CONTEÚDO: `dados` é um useMemo que devolve objeto novo a cada
  // render, então avisar por referência faria pai e filho se re-renderizarem em
  // loop infinito.
  const avisar = useRef(onDadosChange);
  avisar.current = onDadosChange;
  const ultimoEmitido = useRef<string>("");
  useEffect(() => {
    const atual = JSON.stringify(dados);
    if (atual === ultimoEmitido.current) return;
    ultimoEmitido.current = atual;
    avisar.current?.(dados);
  }, [dados]);

  // ---- Sugestões (nunca preenchem sozinhas) --------------------------------
  const doc = (tipo === "PJ" ? f.cnpj : f.cpf) ?? "";
  const docLimpo = doc.replace(/\D/g, "");
  const docCompleto = tipo === "PJ" ? docLimpo.length === 14 : docLimpo.length === 11;

  useEffect(() => {
    if (!docCompleto) { setNaBase(null); setReceita(null); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from("clientes")
        .select("nome, razao_social_da_empresa, nome_completo, email_principal, telefone_principal, logradouro, numero_do_endereco, bairro, cidade, estado_sigla, cep, created_at")
        .eq("cpf_cnpj", docLimpo)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (vivo) setNaBase((data as Record<string, unknown>) ?? null);

      if (tipo === "PJ") {
        const r = await consultarReceita(docLimpo);
        if (vivo) setReceita(r);
      }
    }, 800);
    return () => { vivo = false; clearTimeout(t); };
  }, [docLimpo, docCompleto, tipo]);

  /** Preenche só os campos ainda vazios (usado pelas sugestões em bloco). */
  function preencherVazios(novos: Record<string, string>) {
    setF((prev) => {
      const out = { ...prev };
      for (const [k, v] of Object.entries(novos)) {
        if (!v) continue;
        if ((out[k] ?? "").trim() === "") out[k] = v;
      }
      return out;
    });
  }

  /** Clique explícito num valor sugerido: aí sim substitui o que estiver lá. */
  function usarValor(campo: string, valor: string) {
    if (!valor) return;
    setF((prev) => ({ ...prev, [campo]: valor }));
  }

  function usarCadastro() {
    if (!naBase) return;
    const b = naBase as Record<string, string | null>;
    preencherVazios({
      nome_empresarial: b.razao_social_da_empresa || b.nome || "",
      nome_completo: b.nome_completo || "",
      email_assinatura: b.email_principal || "",
      telefone: b.telefone_principal || "",
      endereco: b.logradouro || "",
      numero: b.numero_do_endereco || "",
      bairro: b.bairro || "",
      cidade: b.cidade || "",
      estado: b.estado_sigla || "",
      cep: (b.cep || "").replace(/\D/g, ""),
    });
  }

  function usarEnderecoDaReceita() {
    if (!receita) return;
    setF((prev) => ({
      ...prev,
      endereco: receita.endereco || prev.endereco,
      numero: receita.numero || prev.numero,
      bairro: receita.bairro || prev.bairro,
      cidade: receita.cidade || prev.cidade,
      estado: receita.uf || prev.estado,
      cep: receita.cep || prev.cep,
    }));
  }

  const receitaInativa = !!receita?.situacao && !/ativa/i.test(receita.situacao);

  /**
   * "Colar a ficha": lê o texto com o MESMO extrator do chat e preenche o que
   * reconhecer. Não sobrescreve campo já preenchido — o que você digitou vale
   * mais que o que veio colado; a tela avisa o que foi mantido.
   */
  function aplicarColado() {
    const extraidos = extrairDadosDaMensagem(colado);
    const novos = dadosParaF(extraidos);
    const entraram: string[] = [];
    const mantidos: string[] = [];

    const atualizado = { ...f };
    for (const [k, v] of Object.entries(novos)) {
      const atual = (atualizado[k] ?? "").trim();
      if (atual && atual !== v) { mantidos.push(k); continue; }
      if (atual === v) continue;
      atualizado[k] = v;
      entraram.push(k);
    }
    setF(atualizado);
    if (String(extraidos.cnpj ?? "").replace(/\D/g, "").length === 14) setTipo("PJ");

    // Links de transcrição vivem fora do `f`; junta sem repetir o que já está lá.
    const linksColados = extraidos.transcricoes ?? [];
    if (linksColados.length) {
      const jaTem = normalizarTranscricoes(transcricoesRaw);
      const novosLinks = linksColados.filter((l) => !jaTem.includes(l));
      if (novosLinks.length) {
        setTranscricoesRaw([...jaTem, ...novosLinks].join("\n"));
        entraram.push("transcricoes");
      }
    }

    setResumoColado(
      entraram.length === 0 && mantidos.length === 0
        ? "Não reconheci nenhum campo nesse texto."
        : `${entraram.length} campo(s) preenchido(s)` +
          (mantidos.length ? ` · ${mantidos.length} já preenchido(s) foram mantidos` : "")
    );
    if (entraram.length) setColado("");
  }

  return (
    <form className={styles.form} onSubmit={submit}>
      {/* Colar ficha ------------------------------------------------------ */}
      <section className={styles.secao}>
        <div className={styles.secaoTitulo}>Colar dados do cliente</div>
        <p className="dim" style={{ fontSize: 13, margin: "0 0 8px" }}>
          Cole a ficha do cliente como ela veio (WhatsApp, e-mail, formulário) e
          o que der para reconhecer é preenchido abaixo. Nada é inventado.
        </p>
        <textarea
          className={styles.colar}
          rows={4}
          value={colado}
          onChange={(e) => { setColado(e.target.value); setResumoColado(null); }}
          placeholder="Ex.: Nome empresarial: ... - CNPJ: ... - CPF: ... - E-mail: ..."
        />
        <div className={styles.colarAcoes}>
          <button type="button" className="btn btn-ghost" onClick={aplicarColado} disabled={!colado.trim()}>
            Preencher com estes dados
          </button>
          {resumoColado && <span className="dim" style={{ fontSize: 13 }}>{resumoColado}</span>}
        </div>
      </section>

      {/* Conferência — sugestões que só entram se você mandar ----------------- */}
      {(naBase || receita) && (
        <section className={styles.secao}>
          <div className={styles.secaoTitulo}>Conferir</div>

          {naBase && (
            <div className={styles.sugestao}>
              <div>
                <strong>Este cliente já está na base</strong>
                <p className="dim" style={{ fontSize: 13, margin: "2px 0 0" }}>
                  {String((naBase as Record<string, string>).razao_social_da_empresa || (naBase as Record<string, string>).nome || "Cadastro existente")}
                </p>
              </div>
              <button type="button" className="btn btn-ghost" onClick={usarCadastro}>
                Preencher com o cadastro
              </button>
            </div>
          )}

          {receita && (
            <div className={styles.sugestao}>
              <div className={styles.sugestaoInfo}>
                <strong>Segundo a Receita Federal</strong>
                {receitaInativa && (
                  <p className={styles.sugestaoAlerta}>
                    Situação cadastral: {receita.situacao}. Confirme com o cliente antes de gerar o contrato.
                  </p>
                )}
                <ul className={styles.sugestaoLista}>
                  {receita.razao_social && (
                    <li>
                      <span>Razão social: {receita.razao_social}</span>
                      <button type="button" className={styles.usar} onClick={() => usarValor("nome_empresarial", receita.razao_social)}>usar</button>
                    </li>
                  )}
                  {receita.endereco && (
                    <li>
                      <span>
                        Endereço: {receita.endereco}
                        {receita.numero ? `, ${receita.numero}` : ""}
                        {receita.bairro ? ` — ${receita.bairro}` : ""}
                        {receita.cidade ? `, ${receita.cidade}/${receita.uf}` : ""}
                      </span>
                      <button type="button" className={styles.usar} onClick={usarEnderecoDaReceita}>usar</button>
                    </li>
                  )}
                  {receita.telefone && (
                    <li>
                      <span>Telefone: {receita.telefone}</span>
                      <button type="button" className={styles.usar} onClick={() => usarValor("telefone", receita.telefone)}>usar</button>
                    </li>
                  )}
                </ul>
                <p className="dim" style={{ fontSize: 12, margin: 0 }}>
                  Endereço e telefone da Receita costumam ser os da contabilidade, não os do
                  cliente — confirme antes de usar. Nada aqui entra sozinho no formulário.
                </p>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Projeto e handoff ------------------------------------------------ */}
      <section className={styles.secao}>
        <div className={styles.secaoTitulo}>Projeto e handoff</div>
        <div className={styles.grid}>
          <label className="field">
            <span>Nome oficial do projeto</span>
            <input
              value={f.nome_projeto ?? ""}
              onChange={set("nome_projeto")}
              placeholder="Ex.: Dra. Tayse Oliveira"
            />
          </label>
        </div>
        {/* Este campo batiza o grupo de WhatsApp do cliente: documento aqui vira
            nome do grupo, do canal do Discord e da pasta do Drive. */}
        {!!f.nome_projeto?.trim() && !nomeProjetoValido(f.nome_projeto) && (
          <p className={styles.hint}>
            Isso não parece nome de projeto. Aqui vai o nome de quem a gente atende
            ("Dra. Tayse Oliveira") — nunca CNPJ, CPF, número ou link. É ele que nomeia o
            grupo de WhatsApp, o ClickUp, o Discord e a pasta do Drive.
          </p>
        )}
        <p className="dim" style={{ fontSize: 13, margin: "6px 0 0" }}>
          É o nome de quem a gente atende, não a razão social. "Clínica Essência LTDA" na
          nota, "Dra. Tayse Oliveira" aqui. Nomeia o projeto no ClickUp, o canal do Discord,
          o grupo de WhatsApp e a pasta do Drive.
          {!f.nome_projeto?.trim() && f.nome_completo?.trim() && (
            <>
              {" "}
              <button
                type="button"
                className="btn btn-ghost"
                style={{ padding: "2px 8px", fontSize: 12 }}
                onClick={() => usarValor("nome_projeto", tituloCase(f.nome_completo ?? ""))}
              >
                usar "{tituloCase(f.nome_completo ?? "")}"
              </button>
            </>
          )}
        </p>

        <div style={{ marginTop: 14 }}>
          <label className="field">
            <span>Transcrição da reunião de fechamento (Google Docs)</span>
            <textarea
              className={styles.colar}
              rows={3}
              value={transcricoesRaw}
              onChange={(e) => setTranscricoesRaw(e.target.value)}
              placeholder={"https://docs.google.com/document/d/…\nUm link por linha"}
            />
          </label>
          <p className="dim" style={{ fontSize: 13, margin: "6px 0 0" }}>
            {transcricoes.length === 0
              ? "Cole o link do Google Docs da transcrição. É com ele que o sistema monta o briefing do cliente para o time, no ClickUp e no grupo de WhatsApp."
              : `${transcricoes.length} link(s) de Google Docs reconhecido(s). Pode colar mais de uma reunião, um por linha.`}
          </p>
        </div>
      </section>

      {/* Tipo & plano ----------------------------------------------------- */}
      <section className={styles.secao}>
        <div className={styles.secaoTitulo}>Tipo & plano</div>
        <div className={styles.tipoLinha}>
          <div className={styles.tipoToggle} role="group" aria-label="Tipo de pessoa">
            <button
              type="button"
              className={`${styles.tipoBtn} ${tipo === "PJ" ? styles.tipoBtnAtivo : ""}`}
              onClick={() => setTipo("PJ")}
            >
              Pessoa Jurídica
            </button>
            <button
              type="button"
              className={`${styles.tipoBtn} ${tipo === "PF" ? styles.tipoBtnAtivo : ""}`}
              onClick={() => setTipo("PF")}
            >
              Pessoa Física
            </button>
          </div>
        </div>
        <div className={styles.grid}>
          <label className="field">
            <span>Plano</span>
            <select value={f.plano ?? ""} onChange={set("plano")}>
              <option value="">Selecione…</option>
              {PLANOS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Valor mensal (R$)</span>
            <input type="number" min="0" step="0.01" value={f.valor_mensal ?? ""} onChange={set("valor_mensal")} placeholder="3500" />
          </label>
          {incluiSocial && (
            <label className="field">
              <span>Volume de posts (por semana)</span>
              <input value={f.volume_posts ?? ""} onChange={set("volume_posts")} placeholder="3" />
            </label>
          )}
        </div>
        {aviso && <p className={styles.aviso}>{aviso}</p>}
      </section>

      {/* Responsável ------------------------------------------------------ */}
      <section className={styles.secao}>
        <div className={styles.secaoTitulo}>Responsável</div>
        <div className={styles.grid}>
          <label className="field">
            <span>Nome completo do responsável</span>
            <input value={f.nome_completo ?? ""} onChange={set("nome_completo")} placeholder="Quem assina o contrato" />
          </label>
          <label className="field">
            <span>CPF do responsável</span>
            <input value={f.cpf ?? ""} onChange={set("cpf")} placeholder="000.000.000-00" inputMode="numeric" />
          </label>
          <label className="field">
            <span>E-mail de assinatura (D4Sign)</span>
            <input type="email" value={f.email_assinatura ?? ""} onChange={set("email_assinatura")} placeholder="responsavel@empresa.com" />
          </label>
          <label className="field">
            <span>Telefone (DDD + número)</span>
            <input value={f.telefone ?? ""} onChange={set("telefone")} placeholder="(11) 99999-9999" inputMode="tel" />
          </label>
        </div>
      </section>

      {/* PJ ou PF --------------------------------------------------------- */}
      {tipo === "PJ" ? (
        <section className={styles.secao}>
          <div className={styles.secaoTitulo}>Empresa (PJ)</div>
          <div className={styles.grid}>
            <label className="field">
              <span>Razão social</span>
              <input value={f.nome_empresarial ?? ""} onChange={set("nome_empresarial")} placeholder="Nome empresarial" />
            </label>
            <label className="field">
              <span>CNPJ</span>
              <input value={f.cnpj ?? ""} onChange={set("cnpj")} placeholder="00.000.000/0000-00" inputMode="numeric" />
            </label>
          </div>
        </section>
      ) : (
        <section className={styles.secao}>
          <div className={styles.secaoTitulo}>Dados pessoais (PF)</div>
          <div className={styles.grid}>
            <label className="field">
              <span>RG</span>
              <input value={f.rg ?? ""} onChange={set("rg")} />
            </label>
            <label className="field">
              <span>Profissão</span>
              <input value={f.profissao ?? ""} onChange={set("profissao")} />
            </label>
            <label className="field">
              <span>Estado civil</span>
              <input value={f.estado_civil ?? ""} onChange={set("estado_civil")} />
            </label>
            <label className="field">
              <span>Nacionalidade</span>
              <input value={f.nacionalidade ?? ""} onChange={set("nacionalidade")} />
            </label>
          </div>
        </section>
      )}

      {/* Cobrança & vigência --------------------------------------------- */}
      <section className={styles.secao}>
        <div className={styles.secaoTitulo}>Cobrança & vigência</div>
        <div className={styles.grid}>
          <label className="field">
            <span>Dia de pagamento (1-31)</span>
            <input type="number" min="1" max="31" value={f.dia_pagamento ?? ""} onChange={set("dia_pagamento")} placeholder="10" />
          </label>
          <label className="field">
            <span>Vigência (meses)</span>
            <input type="number" min="1" value={f.tempo_contrato ?? ""} onChange={set("tempo_contrato")} placeholder="12" />
          </label>
          <label className="field">
            <span>Data de início</span>
            <input type="date" value={f.data_inicio ?? ""} onChange={set("data_inicio")} />
          </label>
        </div>
      </section>

      {/* Endereço --------------------------------------------------------- */}
      <section className={styles.secao}>
        <div className={styles.secaoTitulo}>Endereço</div>
        <div className={styles.grid}>
          <label className="field">
            <span>CEP</span>
            <input value={f.cep ?? ""} onChange={set("cep")} placeholder="00000-000" inputMode="numeric" />
          </label>
          <label className={`field ${styles.span2}`}>
            <span>Logradouro</span>
            <input value={f.endereco ?? ""} onChange={set("endereco")} placeholder="Rua / Avenida" />
          </label>
          <label className="field">
            <span>Número</span>
            <input value={f.numero ?? ""} onChange={set("numero")} />
          </label>
          <label className="field">
            <span>Complemento (opcional)</span>
            <input value={f.complemento ?? ""} onChange={set("complemento")} />
          </label>
          <label className="field">
            <span>Bairro</span>
            <input value={f.bairro ?? ""} onChange={set("bairro")} />
          </label>
          <label className="field">
            <span>Cidade</span>
            <input value={f.cidade ?? ""} onChange={set("cidade")} />
          </label>
          <label className="field">
            <span>UF</span>
            <select value={f.estado ?? ""} onChange={set("estado")}>
              <option value="">--</option>
              {UFS.map((uf) => (
                <option key={uf} value={uf}>{uf}</option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {/* Rodapé ----------------------------------------------------------- */}
      <div className={styles.rodape}>
        {!validacao.ok && <p className={styles.hint}>{validacao.message}</p>}

        {confirmar && validacao.ok ? (
          <div className={styles.confirmBox}>
            <p className={styles.warnGerar}>
              Isto vai criar um <strong>contrato real no D4Sign</strong> e uma <strong>cobrança no Asaas</strong>. Confirmar a geração?
            </p>
            {submitting ? (
              <p className={styles.warnGerar}>Gerando o contrato… acompanhe o progresso no canto da tela.</p>
            ) : (
              <div className={styles.confirmAcoes}>
                <button type="button" className="btn btn-primary" onClick={() => onGerar(dados)}>
                  Confirmar e gerar contrato
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setConfirmar(false)}>
                  Cancelar
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <p className={styles.warnGerar}>
              Ao gerar, isto cria um <strong>contrato real no D4Sign</strong> e uma <strong>cobrança no Asaas</strong>.
            </p>
            <button type="submit" className="btn btn-primary" disabled={!validacao.ok || submitting}>
              Gerar contrato
            </button>
          </>
        )}
      </div>
    </form>
  );
}
